/**
 * FastInfoModal — customer-facing "Learn more" popup for Fast by
 * NoFee, opened from the FastReputationStrip / RestaurantCard badge.
 *
 * iter134 (Jul 2026) — Owner spec: retire the public tier ladder
 * (Standard / Plus / Premium / Thunder), Fast Stars and Fast Badges.
 * Modal now positions Fast by NoFee as an owner-facing trust +
 * operational-monitoring program. Fast Score remains an internal
 * metric visible only inside the restaurant dashboard.
 *
 * Sections rendered (top → bottom):
 *   • ⚡ Fast by NoFee eyebrow + title
 *   • Intro paragraph
 *   • "Managers can:" 7-bullet checklist
 *   • Footer highlight: "Fast by NoFee does not mean the fastest
 *     delivery. It means: Delivered On Time. Verified by Real
 *     Customers."
 *
 * All legacy props (tier / tierLabel / points / pointsToNext /
 * nextTier) are accepted as no-ops so existing call sites keep
 * working during the migration — nothing tier-related is ever shown.
 */
import React, { useEffect } from 'react';
import { Zap, X, CheckCircle2 } from 'lucide-react';

const MANAGER_BULLETS = [
  'Track delivery performance',
  'Monitor verified on-time deliveries',
  'Monitor Fast Score',
  'Improve customer trust',
  'Identify delivery issues faster',
  'Improve operational performance',
  'Manage delivery quality from anywhere',
];

const FastInfoModal = ({ restaurantName, onClose, tier, tierLabel, points, pointsToNext, nextTier }) => {
  // Close on ESC + body scroll lock so the page behind doesn't drift.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      data-testid="fast-info-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[88dvh] overflow-y-auto rounded-2xl border border-yellow-400/30 bg-zinc-950 text-white shadow-[0_25px_60px_-15px_rgba(212,175,55,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          data-testid="fast-info-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.04] hover:bg-white/[0.10] text-white/70 hover:text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header — eyebrow pill + title */}
        <div className="p-6 border-b border-zinc-800">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.18em] mb-3">
            <Zap className="w-3 h-3 text-yellow-400 fill-yellow-400" aria-hidden="true" />
            Fast by NoFee
          </div>
          <h2
            className="text-2xl font-bold leading-tight"
            data-testid="fast-info-modal-title"
          >
            Verified On-Time Delivery
          </h2>
          {restaurantName && (
            <p className="text-xs text-zinc-500 mt-0.5">For {restaurantName}</p>
          )}
          <p className="text-sm text-zinc-300 mt-3 leading-relaxed" data-testid="fast-info-modal-intro">
            Fast by NoFee gives restaurant owners and managers greater visibility into delivery performance.
          </p>
        </div>

        <div className="px-6 pt-5 pb-6 space-y-5 text-sm text-zinc-300">
          {/* Managers can: checklist */}
          <div>
            <h3 className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">
              Managers can:
            </h3>
            <ul className="space-y-2" data-testid="fast-info-modal-bullets">
              {MANAGER_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                  <span className="text-[14px] leading-snug text-zinc-100">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Footer highlight */}
          <div
            className="rounded-xl border border-yellow-400/35 bg-yellow-400/[0.06] p-4"
            data-testid="fast-info-modal-footer"
          >
            <p className="text-[13.5px] text-white/90 leading-relaxed">
              <span className="font-semibold text-white">Fast by NoFee does not mean the fastest delivery.</span>
              <br />
              It means: <span className="font-bold text-yellow-300">Delivered On Time. Verified by Real Customers.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FastInfoModal;
